import {
	ActionBar,
	Dialog,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { ExchangeEmailManagementErrorModel } from "client/Api/Email/common";
import {
	EagerLoadedChildren,
	IFolderViewModel,
	MoveEmailsToFolderModel,
	moveEmailToFolder
} from "client/Api/Email/email";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import {
	FolderSelector,
	FolderSelectorHandle,
	Mailbox
} from "../FolderSelector/FolderSelector";
import { SelectedFolder } from "../FolderSelector/useFolderChildren";
import { useEmailCreateFolderDialog } from "./EmailCreateFolderDialog";

export type MoveEmailsToFolderDialogProps = {
	currentFolderId: string | undefined;
	sourceSmtpAddress: string | null;
	emails: readonly EmailIdWithIsReadStatus[];
	onAfterMove(data: FolderMovedData): void;
	onFolderStructureChange?(): void;
	onClose(): void;
};

export const MoveEmailsToFolderDialog = (props: MoveEmailsToFolderDialogProps): ReactElement => {
	const {
		currentFolderId,
		sourceSmtpAddress,
		emails,
		onAfterMove,
		onClose,
		onFolderStructureChange
	} = props;

	const [selectedFolder, setSelectedFolder] = useState<SelectedFolder>();
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const [emailCreateFolderDialog, openEmailCreateFolderDialog] = useEmailCreateFolderDialog();
	const folderSelectorRef: RefObject<FolderSelectorHandle | null> = useRef<FolderSelectorHandle>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const sourceMailbox = useMemo(
		(): Mailbox | undefined => isNotNil(sourceSmtpAddress)
			? ({
				smtpAddress: sourceSmtpAddress,
				name: sourceSmtpAddress
			})
			: undefined,
		[sourceSmtpAddress]
	);

	const moveHandler = async (): Promise<void> => {
		if (isNil(selectedFolder) || isNil(selectedFolder.id)) {
			throw new Error("You should not be able to click the button");
		}

		const remainsInTheSameFolder: boolean = selectedFolder.id === currentFolderId;
		if (remainsInTheSameFolder) {
			onClose();
			return;
		}

		const moveEmailsToFolderModel: MoveEmailsToFolderModel = {
			destinationFolderId: { id: selectedFolder.id },
			selection: {
				smtpAddress: isNotNil(sourceSmtpAddress)
					? { isSet: true, value: sourceSmtpAddress }
					: { isSet: false },
				emailIds: emails.map((x) => x.emailId)
			}
		};

		setIsBusy(true);

		try {
			await moveEmailToFolder(moveEmailsToFolderModel);
			onAfterMove({
				folderName: selectedFolder.name,
				destinationFolderId: selectedFolder.id
			});

			onClose();
		} catch (e) {
			let handled = false;
			if (e instanceof HttpRequestError) {
				const responseData: ExchangeEmailManagementErrorModel = e.readJson<ExchangeEmailManagementErrorModel>();
				alertManager.showError(responseData.message);
				handled = true;
			}

			if (!handled) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsBusy(false);
		}
	};

	const createNewFolderHandler = useCallback(
		async (parentFolder: SelectedFolder): Promise<void> => {
			const result: TryResult<IFolderViewModel<EagerLoadedChildren>> = await openEmailCreateFolderDialog({
				target: isNotNil(parentFolder.id)
					? {
						kind: "folderId",
						folderId: parentFolder.id,
						mailbox: parentFolder.smtpAddress
					}
					: {
						kind: "mailbox-root",
						mailbox: ensureNotNil(parentFolder.smtpAddress)
					}
			});

			if (result.wasSuccessful) {
				const {
					folderId,
					name
				} = result.result;

				ensureNotNil(folderSelectorRef.current)
					.navigateForwardTo({
						id: folderId.id,
						name: name,
						smtpAddress: parentFolder.smtpAddress
					});

				onFolderStructureChange?.();
			}
		},
		[
			openEmailCreateFolderDialog,
			onFolderStructureChange
		]
	);

	return (
		<>
			<Dialog
				hasBusyIndicator={isBusy}
				trackingId="move-emails-to-folder-dialog"
				variant="left-slider"
				onClose={onClose}
			>
				<Dialog.Header
					title={localize(
						emails.length === 1
							? "_MoveEmailTitle"
							: "_MoveEmailsTitle"
					)}
					onClose={onClose}
					hasBorder
				/>

				<Dialog.Body goesEdgeToEdge>
					<ActionBar
						hasBorderBottom
						trackingId="action-bar"
					>
						{isNotNil(selectedFolder) && hasEitherIdOrMailboxDefined(selectedFolder) && (
							<ActionBar.Button
								icon={{ kind: "svg", name: "folder" }}
								text={localize("_NewFolder")}
								onClick={() => createNewFolderHandler(selectedFolder)}
							/>
						)}
					</ActionBar>
					<FolderSelector
						mailbox={sourceMailbox}
						onChange={setSelectedFolder}
						ref={folderSelectorRef}
					/>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_MoveHere")}
							trackingId="move"
							onClick={moveHandler}
							isDisabled={isNil(selectedFolder?.id)}
						/>
						{!isMobile && (
							<Toolbar.Button
								kind="secondary"
								text={localize("_Cancel")}
								trackingId="cancel"
								onClick={onClose}
							/>
						)}
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{emailCreateFolderDialog}
		</>
	);
};
MoveEmailsToFolderDialog.displayName = "MoveEmailsToFolderDialog";

export interface FolderMovedData {
	folderName: string;
	destinationFolderId: string;
};

export type MoveEmailsToFolderDialogData = Omit<MoveEmailsToFolderDialogProps, "onClose" | "onAfterMove">;

export function useMoveEmailsToFolderDialog(): [
	element: ReactElement | null,
	open: (initialData: MoveEmailsToFolderDialogData) => Promise<TryResult<FolderMovedData>>
] {
	return useDataInputDialog<MoveEmailsToFolderDialogData, FolderMovedData>(
		(initialData, onClose, onAfterMove) => (
			<MoveEmailsToFolderDialog
				{...initialData}
				onClose={onClose}
				onAfterMove={onAfterMove}
			/>
		)
	);
};

function hasEitherIdOrMailboxDefined(folder: SelectedFolder): boolean {
	return isNotNil(folder.id) || isNotNil(folder.smtpAddress);
}